import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type { PlanAuditoria, Auditoria, Hallazgo, EstadoAuditoria, TipoHallazgo } from './models/auditorias-internas.model';

const KEY_PLANES = 'auditorias_planes';
const KEY_AUDITORIAS = 'auditorias_auditorias';
const KEY_HALLAZGOS = 'auditorias_hallazgos';

function demoPlanes(): PlanAuditoria[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  return [
    {
      id: '1',
      anio: new Date().getFullYear(),
      programa: 'Programa anual de auditorías internas SG-SST',
      alcance: 'Sistema de Gestión SST - ISO 45001',
      criterios: 'Normativa local, política SST, procedimientos',
      estado: 'en_curso',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoAuditorias(): Auditoria[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      planId: '1',
      codigo: 'AUD-2025-01',
      fechaProgramada: d(14),
      fechaEjecucion: d(7),
      auditorLider: 'Ana Martínez',
      auditados: 'Producción, Mantenimiento',
      estado: 'informe',
      resultado: 92,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      planId: '1',
      codigo: 'AUD-2025-02',
      fechaProgramada: d(45),
      auditorLider: 'Ana Martínez',
      auditados: 'Almacén, Oficinas',
      estado: 'planificada',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoHallazgos(): Hallazgo[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  return [
    {
      id: '1',
      auditoriaId: '1',
      tipo: 'no_conformidad',
      descripcion: 'Registro de inspecciones de extintores incompleto en zona 2.',
      criterio: 'Procedimiento de inspección de extintores',
      requiereAccion: true,
      cerrado: false,
      createdAt: now,
      updatedAt: now,
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class AuditoriasInternasService {
  private planes: PlanAuditoria[] = demoPlanes();
  private auditorias: Auditoria[] = demoAuditorias();
  private hallazgos: Hallazgo[] = demoHallazgos();

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const [p, a, h] = await Promise.all([
      this.demoStorage.get<PlanAuditoria[]>(KEY_PLANES),
      this.demoStorage.get<Auditoria[]>(KEY_AUDITORIAS),
      this.demoStorage.get<Hallazgo[]>(KEY_HALLAZGOS),
    ]);
    this.planes = Array.isArray(p) ? p : demoPlanes();
    this.auditorias = Array.isArray(a) ? a : demoAuditorias();
    this.hallazgos = Array.isArray(h) ? h : demoHallazgos();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return Promise.all([
      this.demoStorage.set(KEY_PLANES, this.planes),
      this.demoStorage.set(KEY_AUDITORIAS, this.auditorias),
      this.demoStorage.set(KEY_HALLAZGOS, this.hallazgos),
    ]).then(() => {}).catch(() => {});
  }

  getPlanes(): PlanAuditoria[] {
    return [...this.planes];
  }

  getAuditorias(planId?: string): Auditoria[] {
    let list = [...this.auditorias];
    if (planId) list = list.filter((a) => a.planId === planId);
    return list.sort((a, b) => b.fechaProgramada.localeCompare(a.fechaProgramada));
  }

  getHallazgos(auditoriaId?: string): Hallazgo[] {
    let list = [...this.hallazgos];
    if (auditoriaId) list = list.filter((h) => h.auditoriaId === auditoriaId);
    return list.sort((a, b) => (b.cerrado ? 0 : 1) - (a.cerrado ? 0 : 1));
  }

  getPlanById(id: string): PlanAuditoria | null {
    return this.planes.find((p) => p.id === id) ?? null;
  }

  getAuditoriaById(id: string): Auditoria | null {
    return this.auditorias.find((a) => a.id === id) ?? null;
  }

  updatePlan(id: string, data: Partial<Pick<PlanAuditoria, 'programa' | 'alcance' | 'criterios' | 'estado'>>): PlanAuditoria | null {
    const p = this.planes.find((x) => x.id === id);
    if (!p) return null;
    Object.assign(p, data);
    p.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...p };
  }

  private nextCodigo(planId: string): string {
    const anio = new Date().getFullYear();
    const prefijo = `AUD-${anio}-`;
    const list = this.auditorias.filter((a) => a.planId === planId && a.codigo.startsWith(prefijo));
    const nums = list.map((a) => parseInt(a.codigo.replace(prefijo, ''), 10)).filter((n) => !isNaN(n));
    const next = nums.length === 0 ? 1 : Math.max(...nums) + 1;
    return `${prefijo}${String(next).padStart(2, '0')}`;
  }

  createAuditoria(planId: string, data: Omit<Auditoria, 'id' | 'planId' | 'codigo' | 'createdAt' | 'updatedAt'>): Auditoria {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const codigo = this.nextCodigo(planId);
    const nueva: Auditoria = {
      ...data,
      id: 'a' + Date.now(),
      planId,
      codigo,
      createdAt: now,
      updatedAt: now,
    };
    this.auditorias.push(nueva);
    this.persistDemo();
    return { ...nueva };
  }

  updateAuditoria(id: string, data: Partial<Omit<Auditoria, 'id' | 'planId' | 'codigo' | 'createdAt'>>): Auditoria | null {
    const a = this.auditorias.find((x) => x.id === id);
    if (!a) return null;
    Object.assign(a, data);
    a.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...a };
  }

  deleteAuditoria(id: string): boolean {
    const idx = this.auditorias.findIndex((x) => x.id === id);
    if (idx === -1) return false;
    this.hallazgos = this.hallazgos.filter((h) => h.auditoriaId !== id);
    this.auditorias.splice(idx, 1);
    this.persistDemo();
    return true;
  }

  addHallazgo(auditoriaId: string, data: Omit<Hallazgo, 'id' | 'auditoriaId' | 'cerrado' | 'fechaCierre' | 'createdAt' | 'updatedAt'>): Hallazgo {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: Hallazgo = {
      ...data,
      id: 'h' + Date.now(),
      auditoriaId,
      cerrado: false,
      createdAt: now,
      updatedAt: now,
    };
    this.hallazgos.push(nuevo);
    this.persistDemo();
    return { ...nuevo };
  }

  updateHallazgo(id: string, data: Partial<Omit<Hallazgo, 'id' | 'auditoriaId' | 'createdAt'>>): Hallazgo | null {
    const h = this.hallazgos.find((x) => x.id === id);
    if (!h) return null;
    Object.assign(h, data);
    h.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...h };
  }

  cerrarHallazgo(id: string): Hallazgo | null {
    return this.updateHallazgo(id, { cerrado: true, fechaCierre: new Date().toISOString().slice(0, 10) });
  }

  reabrirHallazgo(id: string): Hallazgo | null {
    return this.updateHallazgo(id, { cerrado: false, fechaCierre: undefined });
  }

  deleteHallazgo(id: string): boolean {
    const idx = this.hallazgos.findIndex((x) => x.id === id);
    if (idx === -1) return false;
    this.hallazgos.splice(idx, 1);
    this.persistDemo();
    return true;
  }

  /** Exporta auditorías y hallazgos a CSV para reporte. */
  exportarReporteCSV(): string {
    const headers = ['Código', 'Fecha programada', 'Fecha ejecución', 'Auditor', 'Auditados', 'Estado', 'Resultado %', 'Tipo hallazgo', 'Descripción', 'Criterio', 'Requiere acción', 'Cerrado', 'Fecha cierre'];
    const rows: string[] = [headers.map((h) => `"${h}"`).join(',')];
    for (const a of this.getAuditorias()) {
      const hallazgos = this.getHallazgos(a.id);
      if (hallazgos.length === 0) {
        rows.push([a.codigo, a.fechaProgramada, a.fechaEjecucion ?? '', a.auditorLider, `"${(a.auditados || '').replace(/"/g, '""')}"`, a.estado, a.resultado ?? '', '', '', '', '', '', ''].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
      } else {
        hallazgos.forEach((h, i) => {
          const audFields = i === 0 ? [a.codigo, a.fechaProgramada, a.fechaEjecucion ?? '', a.auditorLider, `"${(a.auditados || '').replace(/"/g, '""')}"`, a.estado, a.resultado ?? ''] : ['', '', '', '', '', '', ''];
          rows.push([...audFields, h.tipo, `"${(h.descripcion || '').replace(/"/g, '""')}"`, `"${(h.criterio || '').replace(/"/g, '""')}"`, h.requiereAccion ? 'Sí' : 'No', h.cerrado ? 'Sí' : 'No', h.fechaCierre ?? ''].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
        });
      }
    }
    return '\uFEFF' + rows.join('\n');
  }
}
